// Modul pro správu editoru nabídky
// Logo tiskárny – stejný asset, jaký se používá v hlavičce aplikace.
// `url:` prefix říká Parcelu 2, aby import vrátil URL k souboru (nikoli inline obsah).
import logoUrl from 'url:../images/logo-icon.svg';
import {
  buildFinishingLines,
  stripFinishingFromName,
  OFFER_FOOTER_DISCLAIMER
} from './shared/offer-item-format';
import { icon } from './icons.js';

const OFFER_STATUS_META = {
  draft:       { label: 'Rozpracovaná', badge: 'bg-gray-100 text-gray-800',       dot: 'bg-gray-500' },
  sent:        { label: 'Odeslána',     badge: 'bg-blue-100 text-blue-800',       dot: 'bg-blue-500' },
  negotiation: { label: 'Vyjednávání',  badge: 'bg-amber-100 text-amber-800',     dot: 'bg-amber-500' },
  approved:    { label: 'Schválena',    badge: 'bg-emerald-100 text-emerald-800', dot: 'bg-emerald-500' },
  rejected:    { label: 'Odmítnuta',    badge: 'bg-red-100 text-red-800',         dot: 'bg-red-500' },
  converted:   { label: 'Převedena',    badge: 'bg-purple-100 text-purple-800',   dot: 'bg-purple-500' }
};
const OFFER_STATUS_ORDER = ['draft', 'sent', 'negotiation', 'approved', 'rejected', 'converted'];

export class OfferEditorManager {
  constructor() {
    this.currentOffer = null;
    this.customerData = '';
    this.customerId = null;
    this.savingCustomer = false;
    this.init();
  }

  init() {
    // Inicializace editoru
  }

  /** Normalizuje customerData (string i objekt z MCP/AI nabídky) na text. */
  normalizeCustomerData(data) {
    if (!data) return '';
    if (typeof data === 'string') return data;
    if (typeof data === 'object') {
      if (window.CustomersManager && typeof window.CustomersManager.formatCustomerToText === 'function') {
        return window.CustomersManager.formatCustomerToText(data);
      }
      const d = data;
      const lines = [];
      if (d.name) lines.push(d.name);
      if (d.address) lines.push(d.address);
      const cityLine = [d.zip, d.city].filter(Boolean).join(' ');
      if (cityLine) lines.push(cityLine);
      const taxLine = [d.ico ? `IČ: ${d.ico}` : '', d.dic ? `DIČ: ${d.dic}` : ''].filter(Boolean).join(', ');
      if (taxLine) { lines.push(''); lines.push(taxLine); }
      return lines.join('\n');
    }
    return String(data);
  }

  async loadOffer(offer) {
    this.currentOffer = offer;
    this.customerData = this.normalizeCustomerData(offer.customerData);
    this.customerId = offer.customerId || null;

    const currentOfferIdElement = document.getElementById('current-offer-id');
    if (currentOfferIdElement) {
      currentOfferIdElement.textContent = offer.id;
    }

    this.renderStatusPanel();
    this.renderOfferContent();
  }

  // ----- Status panel -----

  getApiBase() {
    return window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
      ? 'http://localhost:1234/api'
      : '/api';
  }

  renderStatusPanel() {
    const host = document.getElementById('nabidka-editor-status-panel');
    if (!host || !this.currentOffer) return;

    const status = this.currentOffer.status || 'draft';
    const meta = OFFER_STATUS_META[status] || OFFER_STATUS_META.draft;
    const isConverted = status === 'converted';

    host.innerHTML = `
      <div class="bg-white border border-gray-200 rounded-lg p-4 mb-4 shadow-sm">
        <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div class="flex items-center gap-3">
            <span class="inline-flex items-center text-sm px-3 py-1 rounded-full ${meta.badge}">
              <span class="w-2 h-2 rounded-full mr-2 ${meta.dot}"></span>${this.escape(meta.label)}
            </span>
            ${this.currentOffer.convertedToOrderId ? `
              <span class="text-sm text-gray-600">
                Převedeno na objednávku
                <button id="offer-open-order-btn" class="text-purple-700 hover:underline font-medium">
                  ${this.escape(this.currentOffer.convertedToOrderId)}
                </button>
              </span>
            ` : ''}
          </div>
          <div class="flex flex-wrap items-center gap-2">
            <label class="text-sm text-gray-600">Změnit stav:</label>
            <select id="offer-status-select"
              class="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              ${isConverted ? 'disabled' : ''}>
              ${OFFER_STATUS_ORDER.map((s) => `<option value="${s}" ${s === status ? 'selected' : ''}>${this.escape(OFFER_STATUS_META[s].label)}</option>`).join('')}
            </select>
            <button id="offer-convert-btn"
              class="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white ${status === 'approved' ? 'bg-purple-600 hover:bg-purple-700' : 'bg-gray-300 cursor-not-allowed'} focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
              ${status === 'approved' ? '' : 'disabled'}
              title="${status === 'approved' ? 'Vytvoří objednávku jako kopii nabídky' : 'Nejprve nastavte stav „Schválena"'}">
              ${icon('arrow-right', 'w-4 h-4 mr-2')}
              Převést na objednávku
            </button>
          </div>
        </div>
        ${this.renderStatusHistory(this.currentOffer.statusHistory)}
      </div>
    `;

    document.getElementById('offer-status-select')?.addEventListener('change', (e) =>
      this.changeStatus(e.target.value)
    );
    document.getElementById('offer-convert-btn')?.addEventListener('click', () => this.convertToOrder());
    document.getElementById('offer-open-order-btn')?.addEventListener('click', () => {
      if (window.ordersListManager && this.currentOffer.convertedToOrderId) {
        window.ordersListManager.openDetail(this.currentOffer.convertedToOrderId);
      }
    });
  }

  renderStatusHistory(history) {
    if (!Array.isArray(history) || history.length <= 1) return '';
    const last3 = history.slice(-3).reverse();
    return `
      <details class="mt-3">
        <summary class="text-xs text-gray-500 cursor-pointer hover:text-gray-700">Historie změn stavu (${history.length})</summary>
        <ol class="relative border-l border-gray-200 ml-3 mt-2">
          ${last3.map((h) => {
            const m = OFFER_STATUS_META[h.status] || { label: h.status, dot: 'bg-gray-400' };
            const time = new Date(h.changedAt).toLocaleString('cs-CZ');
            return `
              <li class="ml-4 pb-2">
                <span class="absolute -left-1.5 mt-1.5 w-2.5 h-2.5 rounded-full border border-white ${m.dot}"></span>
                <div class="text-xs font-medium text-gray-800">${this.escape(m.label)}</div>
                <div class="text-xs text-gray-500">${time} · ${this.escape(h.changedBy || '—')}</div>
                ${h.note ? `<div class="text-xs text-gray-600">${this.escape(h.note)}</div>` : ''}
              </li>
            `;
          }).join('')}
        </ol>
      </details>
    `;
  }

  async changeStatus(newStatus) {
    if (!this.currentOffer) return;
    try {
      const res = await fetch(`${this.getApiBase()}/offers/${this.currentOffer.id}/status`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Nepodařilo se změnit stav');
      this.currentOffer = json.data;
      this.renderStatusPanel();
      if (window.showNotification) {
        window.showNotification(`Stav změněn na „${OFFER_STATUS_META[newStatus]?.label || newStatus}"`, 'success');
      }
    } catch (err) {
      console.error('Chyba při změně stavu:', err);
      if (window.showNotification) window.showNotification(err.message, 'error');
      this.renderStatusPanel();
    }
  }

  async convertToOrder() {
    if (!this.currentOffer) return;
    if (this.currentOffer.status !== 'approved') {
      if (window.showNotification) window.showNotification('Pouze schválené nabídky lze převést na objednávku', 'warning');
      return;
    }
    if (!confirm(`Převést nabídku #${this.currentOffer.id} na objednávku?\nNabídka se uzamkne pro další úpravy.`)) return;

    try {
      const res = await fetch(`${this.getApiBase()}/orders/from-offer/${this.currentOffer.id}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Nepodařilo se převést nabídku');

      const order = json.data;
      if (window.showNotification) window.showNotification(`Vytvořena objednávka #${order.id}`, 'success');

      // Refresh nabídky (status converted, convertedToOrderId)
      try {
        const r = await fetch(`${this.getApiBase()}/offers/${this.currentOffer.id}`, { credentials: 'include' });
        if (r.ok) {
          const data = await r.json();
          this.currentOffer = data.data;
          this.renderStatusPanel();
        }
      } catch (e) {
        console.warn('Nepodařilo se obnovit data nabídky po převodu:', e);
      }

      // Otevři detail nově vytvořené objednávky
      if (window.ordersListManager) {
        window.ordersListManager.openDetail(order.id);
      } else if (window.sectionManager) {
        window.sectionManager.showSection('objednavky');
      }
    } catch (err) {
      console.error('Chyba při převodu:', err);
      if (window.showNotification) window.showNotification(err.message, 'error');
    }
  }

  renderOfferContent() {
    if (!this.currentOffer) return;

    const container = document.getElementById('nabidka-editor-content');
    if (!container) return;

    const date = new Date(this.currentOffer.createdAt);
    const formattedDate = date.toLocaleDateString('cs-CZ', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });

    // Vypočítat celkovou cenu a cenu s DPH
    const totalPrice = this.currentOffer.totalPrice || 0;
    const vatRate = 0.21; // 21% DPH
    const totalPriceWithVat = totalPrice * (1 + vatRate);

    container.innerHTML = `
      <div class="container a4-wrapper" style="max-width: 800px; margin: 0 auto; font-family: 'Plus Jakarta Sans Variable', 'Inter Tight', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;">
        <header style="display: flex; flex-direction: row; align-items: flex-start; padding-bottom: 22px; border-bottom: 1px solid black; margin-bottom: 20px;">
          <div style="width: 50%;">
            <img src="${logoUrl}" alt="Tiskárna Tria, s.r.o." width="140" height="45">
          </div>
          <div style="width: 50%; text-align: right;">
            <h1 style="font-size: 1.3em; margin: 0 0 10px 0; font-weight: 600;">Cenová nabídka č. ${this.currentOffer.id}</h1>
            <div style="font-size: 0.9em; color: #5A6079;">Ze dne: ${formattedDate}</div>
            <div style="font-size: 0.9em; color: #5A6079;">Vystavil: ${this.currentOffer.createdBy || 'Systém'}</div>
          </div>
        </header>

        <section style="display: flex; padding-top: 10px; flex-direction: row; align-items: flex-start; justify-content: flex-start; margin-bottom: 20px;">
          <div style="width: 50%;">
            <div class="flex items-center justify-between mb-2">
              <p class="offer-party-label">Odběratel</p>
              <div class="flex items-center gap-2">
                ${this.customerId ? `
                  <span class="inline-flex items-center text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700" title="Propojeno se zákazníkem">
                    ${icon('check', 'w-3 h-3 mr-1')}
                    Z databáze
                  </span>
                  <button type="button" onclick="window.offerEditorManager.clearCustomer()"
                    class="text-xs text-gray-500 hover:text-red-600 underline">Odpojit</button>
                ` : ''}
                <button type="button" onclick="window.offerEditorManager.openCustomerSelector()"
                  class="inline-flex items-center px-2.5 py-1 border border-blue-300 text-xs font-medium rounded-md text-blue-700 bg-white hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-blue-500">
                  ${icon('magnifying-glass', 'w-3.5 h-3.5 mr-1')}
                  ${this.customerId ? 'Změnit zákazníka' : 'Vybrat zákazníka'}
                </button>
              </div>
            </div>
            <div class="customer-data-container">
              <textarea id="customer-data-input" 
                class="w-full p-3 border border-gray-300 rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" 
                rows="8" 
                placeholder="Zadejte údaje odběratele:&#10;Název společnosti&#10;Adresa&#10;PSČ Město&#10;Země&#10;&#10;IČ: xxxxxxxxx&#10;DIČ: xxxxxxxxx"
                onchange="window.offerEditorManager.updateCustomerData(this.value)"
              >${this.customerData}</textarea>
            </div>
          </div>

          <div style="width: 50%; padding-left: 20px;">
            <p class="offer-party-label">Dodavatel</p>
            <div>
              <p>Tiskárna Tria s.r.o.<br>
              Olbramovice Ves 31,<br>
              259 01 Olbramovice</p>
              <p>IČO: 49546112<br>
              DIČ: CZ49546112</p>
              <p>Email: tiskarna@tria.cz<br>
              Web: www.tria.cz</p>
            </div>
          </div>
        </section>

        <table style="width: 100%; border-collapse: collapse; margin-top: 10px; margin-bottom: 20px;">
          <thead>
            <tr style="border-bottom: 1px solid black;">
              <th style="padding: 8px 4px; text-align: left; font-size: 0.85em; letter-spacing: 0.04em; text-transform: uppercase; color: #5A6079;">Označení položky</th>
              <th style="padding: 8px 4px; text-align: right; font-size: 0.85em; letter-spacing: 0.04em; text-transform: uppercase; color: #5A6079; white-space: nowrap;">Počet kusů</th>
              <th style="padding: 8px 4px; text-align: right; font-size: 0.85em; letter-spacing: 0.04em; text-transform: uppercase; color: #5A6079; white-space: nowrap;">Cena za kus</th>
              <th style="padding: 8px 4px; text-align: right; font-size: 0.85em; letter-spacing: 0.04em; text-transform: uppercase; color: #5A6079;">Cena</th>
              <th style="padding: 8px 4px; text-align: right; font-size: 0.85em; letter-spacing: 0.04em; text-transform: uppercase; color: #5A6079;">% DPH</th>
              <th style="padding: 8px 4px; text-align: right; font-size: 0.85em; letter-spacing: 0.04em; text-transform: uppercase; color: #5A6079; white-space: nowrap;">Cena s DPH</th>
            </tr>
          </thead>
          <tbody>
            ${this.currentOffer.items.map(item => this.renderOfferItemRow(item)).join('')}
          </tbody>
        </table>

        <section style="display: flex; justify-content: flex-end; margin-bottom: 16px;">
          <div style="min-width: 280px; font-size: 1em;">
            <div style="display: flex; justify-content: space-between; padding: 4px 0;">
              <span style="color: #5A6079;">Cena celkem (bez DPH)</span>
              <span>${totalPrice.toLocaleString('cs-CZ')} Kč</span>
            </div>
            <div style="display: flex; justify-content: space-between; padding: 4px 0;">
              <span style="color: #5A6079;">DPH ${(vatRate * 100).toFixed(0)} %</span>
              <span>${(totalPrice * vatRate).toLocaleString('cs-CZ', { maximumFractionDigits: 2 })} Kč</span>
            </div>
            <div style="display: flex; justify-content: space-between; padding: 8px 0 4px; margin-top: 4px; border-top: 1px solid black; font-weight: 700; font-size: 1.15em;">
              <span>Celkem s DPH</span>
              <span>${totalPriceWithVat.toLocaleString('cs-CZ', { maximumFractionDigits: 2 })} Kč</span>
            </div>
          </div>
        </section>

        <footer style="margin-top: 24px; padding-top: 16px; border-top: 1px solid #EDEEF3; font-size: 0.75rem; color: #5A6079; line-height: 1.5; text-align: center;">
          ${OFFER_FOOTER_DISCLAIMER}
        </footer>
      </div>
    `;
  }

  renderOfferItemRow(item) {
    const vatRate = 0.21; // 21% DPH
    const priceWithVat = item.price * (1 + vatRate);
    const quantity = Number(item.formData?.pocetKusu) || 1;
    // Cena za kus – preferujeme uložený pricePerPiece (kalkulačka ho spočítá
    // včetně všech příplatků), jinak fallback na item.price / počet kusů.
    const unitPrice = item.pricePerPiece || (quantity > 0 ? item.price / quantity : 0);
    const displayName = stripFinishingFromName(item.name || '');
    const finishingLines = item.formData ? buildFinishingLines(item.formData) : [];
    const hasSpec = finishingLines.length > 0;
    const nameId = (item.name || '').replace(/[^a-zA-Z0-9]/g, '');

    return `
      <tr class="${hasSpec ? 'item-row' : ''}" style="${hasSpec ? '' : 'border-bottom: 1px solid #EDEEF3;'}">
        <td style="padding: 8px 4px; vertical-align: top; ${hasSpec ? '' : ''}">
          <div style="font-weight: 500;">${this.escape(displayName)}</div>
          ${item.notes && item.notes.trim() ? `<div style="font-size: 0.85em; color: #5A6079; margin-top: 2px;">${this.escape(item.notes)}</div>` : ''}
        </td>
        <td style="padding: 8px 4px; text-align: right; white-space: nowrap; vertical-align: top; font-variant-numeric: tabular-nums;">${quantity.toLocaleString('cs-CZ')} ks</td>
        <td style="padding: 8px 4px; text-align: right; white-space: nowrap; vertical-align: top; font-variant-numeric: tabular-nums;">${unitPrice.toLocaleString('cs-CZ', { maximumFractionDigits: 2, minimumFractionDigits: 2 })} Kč</td>
        <td style="padding: 8px 4px; text-align: right; white-space: nowrap; vertical-align: top; font-variant-numeric: tabular-nums;">${item.price.toLocaleString('cs-CZ', { maximumFractionDigits: 2 })} Kč</td>
        <td style="padding: 8px 4px; text-align: right; white-space: nowrap; vertical-align: top; font-variant-numeric: tabular-nums;">${(vatRate * 100).toFixed(0)} %</td>
        <td style="padding: 8px 4px; text-align: right; white-space: nowrap; vertical-align: top; font-variant-numeric: tabular-nums;">${priceWithVat.toLocaleString('cs-CZ', { maximumFractionDigits: 2 })} Kč</td>
      </tr>
      ${hasSpec ? `
        <tr style="border-bottom: 1px solid #EDEEF3;">
          <td colspan="6" style="padding: 0 4px 10px 4px; font-size: 0.85em; color: #5A6079; line-height: 1.5;">
            ${finishingLines.map((l) => this.escape(l)).join('<br>')}
          </td>
        </tr>
      ` : ''}
      <tr style="border-bottom: 1px solid #EDEEF3;">
        <td colspan="6" style="padding: 0 4px 12px 4px;">
          <textarea id="notes-${nameId}"
            style="width: 100%; min-height: 52px; padding: 6px 8px; border: 1px solid #D6D9E3; border-radius: 4px; resize: vertical; font-size: 0.9em; color: #0B0E2D;"
            placeholder="Zadejte dodatečné informace nebo poznámky k této položce..."
            onchange="window.offerEditorManager.updateItemNotes('${this.escape(item.name)}', this.value)"
          >${this.escape(item.notes || '')}</textarea>
        </td>
      </tr>
    `;
  }

  // Zpětně-kompatibilní wrapper: vrací HTML s <br>. Nová logika je v
  // shared/offer-item-format.js. Dodatečné mapování níže zůstává pro případ,
  // že by někdo volal getLakText apod. z externího kódu.
  formatItemSpecification(formData) {
    return buildFinishingLines(formData || {}).join('<br>');
  }

  _legacyFormatItemSpecification(formData) {
    let spec = [];

    // Digitální speciální barvy
    if (formData.barvyDigital && formData.barvyDigital !== '0') {
      const digitalText = this.getDigitalColorName(formData.barvyDigital);
      if (digitalText) {
        spec.push(`Digitální speciální barva: ${digitalText}`);
      }
    }

    // Lakování
    if (formData.lakovani && formData.lakovani !== '0') {
      const lakText = this.getLakText(formData.lakovani);
      if (lakText) {
        spec.push(`Lakování: ${lakText}`);
      }
    }

    // Laminace
    if (formData.laminace && formData.laminace !== '0') {
      const laminaceText = this.getLaminaceText(formData.laminace);
      if (laminaceText) {
        spec.push(`Laminace: ${laminaceText}`);
      }
    }

    // Falcování
    if (formData.falcovani && formData.falcovani !== '0') {
      const falcovaniText = this.getFalcovaniText(formData.falcovani);
      if (falcovaniText) {
        spec.push(`Falcování: ${falcovaniText}`);
      }
    }

    // Bigování
    if (formData.bigovani && formData.bigovani !== '0') {
      const bigovaniText = this.getBigovaniText(formData.bigovani);
      if (bigovaniText) {
        spec.push(`Bigování: ${bigovaniText}`);
      }
    }

    // Výsek
    if (formData.vysek && formData.vysek !== '0') {
      const vysekText = this.getVysekText(formData.vysek);
      const pozicNaVysek = formData.pozicNaVysek || '1';
      if (vysekText) {
        spec.push(`Výsek: ${vysekText} (${pozicNaVysek} pozic)`);
      }
    }

    // Předsek
    if (formData.predsek && formData.predsek !== '0') {
      const predsekText = this.getPredsekText(formData.predsek);
      const pozicNaPredsek = formData.pozicNaPredsek || '1';
      if (predsekText) {
        spec.push(`Předsek: ${predsekText} (${pozicNaPredsek} pozic)`);
      }
    }

    // Ražba
    if (formData.razba && formData.razba !== '0') {
      const razbaText = this.getRazbaText(formData.razba);
      const pozicNaRazbu = formData.pozicNaRazbu || '1';
      if (razbaText) {
        spec.push(`Ražba: ${razbaText} (${pozicNaRazbu} pozic)`);
      }
    }

    // Vrtání
    if (formData.vrtani && formData.vrtani !== '0') {
      const vrtaniText = this.getVrtaniText(formData.vrtani);
      if (vrtaniText) {
        spec.push(`Vrtání: ${vrtaniText}`);
      }
    }

    // Perforace
    if (formData.perforace && formData.perforace !== '0') {
      const pocetPerforaci = parseInt(formData.perforace);
      if (pocetPerforaci > 0) {
        spec.push(`Perforace ${pocetPerforaci}x`);
      }
    }

    // Lepení
    if (formData.lepeni && formData.lepeni !== '0') {
      const lepeniText = this.getLepeniText(formData.lepeni);
      if (lepeniText) {
        spec.push(lepeniText);
      }
    }

    // Vazba
    if (formData.vazba && formData.vazba !== '0') {
      const vazbaText = this.getVazbaText(formData.vazba);
      if (vazbaText) {
        spec.push(vazbaText);
      }
    }

    return spec.join('<br>');
  }

  // Pomocné funkce pro převod hodnot na texty (shodné s offer-items.js)
  getDigitalColorName(typ) {
    const nazvy = {
      'lak_10_50': '1/0 lak (parciální)',
      'lak_11_50': '1/1 lak (parciální)',
      'lak_10_100': '1/0 lak (celá plocha)',
      'lak_11_100': '1/1 lak (celá plocha)',
      'bila_10_50': '1/0 bílá (parciální)',
      'bila_11_50': '1/1 bílá (parciální)',
      'bila_10_100': '1/0 bílá (celá plocha)',
      'bila_11_100': '1/1 bílá (celá plocha)',
      'zlata_10_50': '1/0 zlatá (parciální)',
      'zlata_11_50': '1/1 zlatá (parciální)',
      'zlata_10_100': '1/0 zlatá (celá plocha)',
      'zlata_11_100': '1/1 zlatá (celá plocha)'
    };
    return nazvy[typ] || '';
  }

  getLakText(typ) {
    const nazvy = {
      'parcial10': '1/0 UV lak parciální',
      'parcial11': '1/1 UV lak parciální',
      'plocha10': '1/0 UV lak celá plocha',
      'plocha11': '1/1 UV lak celá plocha',
      'stroj10': '1/0 Stroj lak',
      'stroj11': '1/1 Stroj lak'
    };
    return nazvy[typ] || '';
  }

  getLaminaceText(typ) {
    const nazvy = {
      'lesk10': '1/0 Lamino lesklé',
      'lesk11': '1/1 Lamino lesklé',
      'mat10': '1/0 Lamino matné',
      'mat11': '1/1 Lamino matné',
      'struktura10': '1/0 Lamino nepoškrabatelné',
      'struktura11': '1/1 Lamino nepoškrabatelné',
      'samet10': '1/0 Lamino sametové',
      'samet11': '1/1 Lamino sametové',
      'kapsaA5': '1/1 Laminace kapsová (A5)',
      'kapsaA4': '1/1 Laminace kapsová (A4)',
      'kapsaA3': '1/1 Laminace kapsová (A3)'
    };
    return nazvy[typ] || '';
  }

  getFalcovaniText(typ) {
    const nazvy = {
      'half-fold': 'Na polovinu (1 lom)',
      'cross-fold': 'Křížový lom (2 lomy)',
      'u-fold': 'Do U (2 lomy)',
      'z-fold': 'Do Z (2 lomy)',
      'double-half-fold': 'Na polovinu (2 lomy)',
      'roll-fold': 'Harmonikové s přebalem (3 lomy)',
      'harmonium-fold-3': 'Harmonikové skládání (3 lomy)',
      'harmonium-fold-4': 'Harmonikové skládání (4 lomy)',
      'harmonium-fold-5': 'Harmonikové skládání (5 lomů)',
      'gate-fold': 'Skládání do psaníčka (3 lomy)',
      'double-gate-fold': 'Zavřené psaníčko (3 lomy)'
    };
    return nazvy[typ] || '';
  }

  getBigovaniText(typ) {
    const nazvy = {
      'half-fold': 'Na polovinu (1 big)',
      'cross-fold': 'Do kříže (2 bigy)',
      'u-fold': 'Do U (2 bigy)',
      'z-fold': 'Do Z (2 bigy)',
      'double-half-fold': 'Na polovinu (2 bigy)',
      'roll-fold': 'Harmonikové (3 bigy)',
      'harmonium-fold-3': 'Harmonikové (3 bigy)',
      'harmonium-fold-4': 'Harmonikové (4 bigy)',
      'harmonium-fold-5': 'Harmonikové (5 bigy)',
      'gate-fold': 'Skládání do psaníčka (3 bigy)',
      'double-gate-fold': 'Zavřené psaníčko (3 bigy)'
    };
    return nazvy[typ] || '';
  }

  getVysekText(option) {
    const nazvy = {
      'vlastni': 'Vlastní/půjčené výsekové železo',
      'nove': 'Nové výsekové železo'
    };
    return nazvy[option] || '';
  }

  getPredsekText(option) {
    const nazvy = {
      '1': 'Vlastní/půjčené předsekové železo',
      '2': 'Nové předsekové železo'
    };
    return nazvy[option] || '';
  }

  getRazbaText(option) {
    const nazvy = {
      '1': 'Vlastní/půjčený štoček',
      '2': 'Nový štoček'
    };
    return nazvy[option] || '';
  }

  getVrtaniText(typ) {
    const nazvy = {
      '1dira2mm': '1 díra (2 mm)',
      '1dira4mm': '1 díra (4 mm)',
      '1dira5mm': '1 díra (5,5 mm)',
      '2diry2mm': '2 díry (2 mm)',
      '2diry4mm': '2 díry (4 mm)',
      '2diry5mm': '2 díry (5,5 mm)',
      'euro2diry2mm': 'Euro blok, 2 díry (2 mm)',
      'euro2diry4mm': 'Euro blok, 2 díry (4 mm)',
      'euro2diry5mm': 'Euro blok, 2 díry (5,5 mm)',
      '3diry2mm': '3 díry (2 mm)',
      '3diry4mm': '3 díry (4 mm)',
      '3diry5mm': '3 díry (5,5 mm)',
      'euro4diry2mm': 'Euro blok, 4 díry (2 mm)',
      'euro4diry4mm': 'Euro blok, 4 díry (4 mm)',
      'euro4diry5mm': 'Euro blok, 4 díry (5,5 mm)'
    };
    return nazvy[typ] || '';
  }

  getLepeniText(typ) {
    const nazvy = {
      '1': 'Lepení krabiček'
    };
    return nazvy[typ] || '';
  }

  getVazbaText(typ) {
    const nazvy = {
      'V1-strojni': 'V1 (2 skoby) strojní',
      'V1-rucni': 'V1 (2 skoby) ručně',
      'blok': 'Lepený blok',
      'blokPrebal': 'Lepený blok s přebalem',
      'V2-PUR': 'V2 lepená (PUR)',
      'V3': 'V3 lepená a šitá',
      'V6': 'V6 leporelo',
      'krouzek-blok': 'Kroužková twin wire',
      'krouzek-kalendar': 'Kroužková s háčkem na pověšení',
      'V4': 'V4 šitá, měkká obálka',
      'V5': 'V5 šitá, tvrdá obálka',
      'V2V8': 'V2V8 – falešná V8',
      'V8': 'V8 šitá, tvrdá obálka'
    };
    return nazvy[typ] || 'Neznámá vazba';
  }

  updateCustomerData(value) {
    this.customerData = value;
    // Pokud uživatel ručně upraví text, propojení se zákazníkem už nereflektuje databázi - rozpojit
    if (this.customerId) {
      this.customerId = null;
    }
    this.persistCustomerOnOffer();
  }

  async openCustomerSelector() {
    if (!window.customersManager) {
      if (window.showNotification) window.showNotification('Modul zákazníků není dostupný', 'error');
      return;
    }
    try {
      const customer = await window.customersManager.openSelector();
      if (!customer) return;
      this.applySelectedCustomer(customer);
    } catch (err) {
      console.error('Chyba při výběru zákazníka:', err);
      if (window.showNotification) window.showNotification(err.message || 'Chyba při výběru zákazníka', 'error');
    }
  }

  applySelectedCustomer(customer) {
    this.customerId = customer.id;
    this.customerData = window.CustomersManager
      ? window.CustomersManager.formatCustomerToText(customer)
      : [customer.name, customer.address, [customer.zip, customer.city].filter(Boolean).join(' ')].filter(Boolean).join('\n');

    const textarea = document.getElementById('customer-data-input');
    if (textarea) textarea.value = this.customerData;

    this.renderOfferContent();
    this.persistCustomerOnOffer();

    if (window.showNotification) {
      window.showNotification(`Vybrán zákazník: ${customer.name}`, 'success');
    }
  }

  clearCustomer() {
    this.customerId = null;
    this.customerData = '';
    const textarea = document.getElementById('customer-data-input');
    if (textarea) textarea.value = '';
    this.renderOfferContent();
    this.persistCustomerOnOffer();
  }

  /**
   * Uloží customerData a customerId do nabídky na backendu (pokud je nabídka načtená).
   */
  async persistCustomerOnOffer() {
    if (!this.currentOffer || this.savingCustomer) return;
    this.savingCustomer = true;
    try {
      const API_BASE_URL = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
        ? 'http://localhost:1234/api'
        : '/api';
      const res = await fetch(`${API_BASE_URL}/offers/${this.currentOffer.id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customerData: this.customerData,
          customerId: this.customerId
        })
      });
      if (!res.ok) {
        throw new Error('Chyba při ukládání odběratele');
      }
      const json = await res.json();
      if (json && json.data) {
        this.currentOffer.customerData = json.data.customerData;
        this.currentOffer.customerId = json.data.customerId;
      }
    } catch (err) {
      console.error('Chyba při ukládání odběratele:', err);
      if (window.showNotification) window.showNotification(err.message || 'Chyba při ukládání odběratele', 'error');
    } finally {
      this.savingCustomer = false;
    }
  }

  updateItemNotes(itemName, notes) {
    if (!this.currentOffer) return;
    
    // Najít položku a aktualizovat poznámky
    const item = this.currentOffer.items.find(item => item.name === itemName);
    if (item) {
      item.notes = notes;
      // Zde by se mohla implementovat automatické ukládání
    }
  }

  async editCurrentOffer() {
    if (!this.currentOffer) return;

    try {
      // Přepnout na kalkulátor s načtenými daty
      if (window.sectionManager) {
        window.sectionManager.showSection('kalkulator');
      }

      // Načíst položky do kalkulátoru
      if (window.offerItemsManager) {
        // Vymazat stávající položky
        window.offerItemsManager.items = [];
        window.offerItemsManager.itemIdCounter = 0;
        window.offerItemsManager.activeItemId = null;

        // Načíst položky z nabídky
        this.currentOffer.items.forEach((item, index) => {
          if (index === 0) {
            // První položku vytvoříme pomocí addItem
            window.offerItemsManager.addItem();
            const firstItem = window.offerItemsManager.items[0];
            firstItem.name = item.name;
            firstItem.formData = item.formData;
            firstItem.price = item.price;
            firstItem.pricePerPiece = item.pricePerPiece;
          } else {
            // Další položky přidáme
            const itemId = ++window.offerItemsManager.itemIdCounter;
            window.offerItemsManager.items.push({
              id: itemId,
              name: item.name,
              formData: item.formData,
              price: item.price,
              pricePerPiece: item.pricePerPiece
            });
          }
        });

        // Aktivovat první položku
        if (window.offerItemsManager.items.length > 0) {
          window.offerItemsManager.activeItemId = window.offerItemsManager.items[0].id;
          window.offerItemsManager.restoreFormState(window.offerItemsManager.items[0].formData);
          window.offerItemsManager.renderItems();
          window.offerItemsManager.updateTotalPrice();
        }
      }

      if (window.showNotification) {
        window.showNotification('Nabídka načtena do kalkulátoru pro editaci', 'success');
      }
    } catch (error) {
      console.error('Chyba při editaci nabídky:', error);
      if (window.showNotification) {
        window.showNotification('Chyba při editaci nabídky', 'error');
      }
    }
  }

  async deleteCurrentOffer() {
    if (!this.currentOffer) return;

    if (!confirm('Opravdu chcete smazat tuto nabídku?')) {
      return;
    }

    try {
      const API_BASE_URL = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' 
        ? 'http://localhost:1234/api' 
        : '/api';
      const response = await fetch(`${API_BASE_URL}/offers/${this.currentOffer.id}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Chyba při mazání nabídky');
      }

      if (window.showNotification) {
        window.showNotification('Nabídka byla úspěšně smazána', 'success');
      }

      // Přepnout zpět na seznam nabídek
      if (window.sectionManager) {
        window.sectionManager.showSection('nabidky');
      }

      // Znovu načíst seznam nabídek
      if (window.offersListManager) {
        window.offersListManager.loadOffers();
      }
    } catch (error) {
      console.error('Chyba při mazání nabídky:', error);
      if (window.showNotification) {
        window.showNotification('Chyba při mazání nabídky', 'error');
      }
    }
  }

  async createPublicLink() {
    if (!this.currentOffer) return;

    try {
      const API_BASE_URL = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' 
        ? 'http://localhost:1234/api' 
        : '/api';
      
      const response = await fetch(`${API_BASE_URL}/offers/${this.currentOffer.id}/public-link`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Chyba při vytváření veřejného odkazu');
      }

      const result = await response.json();
      
      if (result.success && result.data) {
        // Zobrazit modal s veřejným odkazem
        this.showPublicLinkModal(result.data);
      } else {
        throw new Error('Neplatná odpověď od serveru');
      }
    } catch (error) {
      console.error('Chyba při vytváření veřejného odkazu:', error);
      if (window.showNotification) {
        window.showNotification('Chyba při vytváření veřejného odkazu', 'error');
      }
    }
  }

  showPublicLinkModal(linkData) {
    // Vytvořit modal pro zobrazení veřejného odkazu
    const modalHtml = `
      <div id="public-link-modal" class="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="public-link-modal-title" role="dialog" aria-modal="true">
        <div class="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
          <!-- Background overlay -->
          <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true"></div>

          <!-- Modal panel -->
          <div class="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
            <div>
              <div class="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
                ${icon('link', 'h-6 w-6 text-green-600')}
              </div>
              <div class="mt-3 text-center sm:mt-5">
                <h3 class="text-lg leading-6 font-medium text-gray-900" id="public-link-modal-title">
                  Veřejný odkaz na nabídku
                </h3>
                <div class="mt-2">
                  <p class="text-sm text-gray-500">
                    Tento odkaz můžete poslat klientovi. Nabídka bude zobrazena bez možnosti editace.
                  </p>
                </div>
                <div class="mt-4">
                  <div class="flex rounded-md shadow-sm">
                    <input 
                      type="text" 
                      id="public-link-url" 
                      readonly 
                      value="${linkData.url}" 
                      class="flex-1 min-w-0 block w-full px-3 py-2 rounded-none rounded-l-md border border-gray-300 bg-gray-50 text-sm"
                    >
                    <button 
                      onclick="window.offerEditorManager.copyToClipboard('${linkData.url}')"
                      type="button" 
                      class="inline-flex items-center px-4 py-2 border border-l-0 border-gray-300 rounded-r-md bg-blue-600 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      ${icon('document-duplicate', 'h-5 w-5')}
                      <span class="ml-2">Kopírovat</span>
                    </button>
                  </div>
                </div>
                <div class="mt-3 text-xs text-gray-400">
                  <p>Vytvořeno: ${new Date(linkData.createdAt).toLocaleString('cs-CZ')}</p>
                  ${linkData.viewCount > 0 ? `<p>Zobrazení: ${linkData.viewCount}×</p>` : ''}
                </div>
              </div>
            </div>
            <div class="mt-5 sm:mt-6">
              <button 
                onclick="window.offerEditorManager.closePublicLinkModal()" 
                type="button" 
                class="inline-flex justify-center w-full rounded-md border border-transparent shadow-sm px-4 py-2 bg-gray-600 text-base font-medium text-white hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 sm:text-sm"
              >
                Zavřít
              </button>
            </div>
          </div>
        </div>
      </div>
    `;

    // Přidat modal do stránky
    const modalContainer = document.createElement('div');
    modalContainer.innerHTML = modalHtml;
    document.body.appendChild(modalContainer);
  }

  async copyToClipboard(text) {
    try {
      await navigator.clipboard.writeText(text);
      if (window.showNotification) {
        window.showNotification('Odkaz byl zkopírován do schránky', 'success');
      }
    } catch (error) {
      console.error('Chyba při kopírování:', error);
      if (window.showNotification) {
        window.showNotification('Chyba při kopírování do schránky', 'error');
      }
    }
  }

  closePublicLinkModal() {
    const modal = document.getElementById('public-link-modal');
    if (modal && modal.parentElement) {
      modal.parentElement.remove();
    }
  }

  async generatePDF() {
    if (!this.currentOffer) return;

    const btn = document.getElementById('nabidka-editor-pdf-btn');
    const originalHtml = btn ? btn.innerHTML : null;
    if (btn) {
      btn.disabled = true;
      btn.innerHTML = `
        ${icon('spinner', 'animate-spin w-4 h-4 mr-2')}
        Generuji PDF…
      `;
    }

    const filename = `nabidka-${this.currentOffer.id}.pdf`;

    try {
      const API_BASE_URL =
        window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
          ? 'http://localhost:1234/api'
          : '/api';

      const response = await fetch(
        `${API_BASE_URL}/offers/${encodeURIComponent(this.currentOffer.id)}/pdf`,
        { credentials: 'include' }
      );

      if (!response.ok) {
        let message = 'Chyba při generování PDF na serveru';
        try {
          const body = await response.json();
          if (body?.error) message = body.error;
        } catch (_) { /* není JSON */ }
        throw new Error(message);
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Necháme URL chvíli živou, aby browser stihl zahájit download, pak ji uvolníme
      setTimeout(() => URL.revokeObjectURL(url), 1000);

      if (window.showNotification) {
        window.showNotification(`PDF „${filename}" bylo staženo`, 'success');
      }
    } catch (error) {
      console.error('Chyba při generování PDF:', error);
      if (window.showNotification) {
        window.showNotification(error.message || 'Chyba při generování PDF', 'error');
      }
    } finally {
      if (btn && originalHtml !== null) {
        btn.disabled = false;
        btn.innerHTML = originalHtml;
      }
    }
  }

  printOffer() {
    if (!this.currentOffer) return;

    try {
      // Vytvořit nové okno s obsahem pro tisk
      const printWindow = window.open('', '_blank');
      const printContent = this.generatePDFContent();

      printWindow.document.write(printContent);
      // Stejné @font-face jako v aplikaci (stylesheet bundlu z app.js) – tisková okna nemají import z node_modules
      window.document.querySelectorAll('link[rel="stylesheet"][href]').forEach((link) => {
        const l = printWindow.document.createElement('link');
        l.rel = 'stylesheet';
        l.href = link.href;
        printWindow.document.head.appendChild(l);
      });
      printWindow.document.close();
      
      // Automaticky spustit tisk
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
      }, 250);

      if (window.showNotification) {
        window.showNotification('Nabídka se odesílá na tisk', 'success');
      }
    } catch (error) {
      console.error('Chyba při tisku nabídky:', error);
      if (window.showNotification) {
        window.showNotification('Chyba při tisku nabídky', 'error');
      }
    }
  }

  escape(str) {
    if (str == null) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  generatePDFContent() {
    if (!this.currentOffer) return '';

    const date = new Date(this.currentOffer.createdAt);
    const formattedDate = date.toLocaleDateString('cs-CZ', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });

    // Vypočítat celkovou cenu a cenu s DPH
    const totalPrice = this.currentOffer.totalPrice || 0;
    const vatRate = 0.21; // 21% DPH
    const totalPriceWithVat = totalPrice * (1 + vatRate);

    // Pro tiskové okno (window.open) potřebujeme absolutní URL k logu,
    // protože nové okno má about:blank jako base a relativní cesty by se nevyřešily.
    const absoluteLogoUrl = new URL(logoUrl, window.location.href).href;

    return `
<!doctype html>
<html>
<head>
    <meta charset='utf-8'>
    <title>Nabídka č. ${this.currentOffer.id}</title>
    <style>
        body {
            font-family: 'Plus Jakarta Sans Variable', 'Inter Tight', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            margin: 40px;
        }
        header {
            display: flex;
            flex-direction: row;
            align-items: flex-start;
            padding-bottom: 22px;
            border-bottom: 1px solid black;
        }
        .section {
            margin-bottom: 20px;
        }
        section.od {
            display: flex;
            padding-top: 10px;
            flex-direction: row;
            align-items: flex-start;
            justify-content: flex-start;
        }
        table {
            width: 100%;
            border-collapse: collapse;
        }
        tbody {
            border-top: 1px solid black;
        }
        .tabulka {
            margin-top: 10px;
            margin-bottom: 0;
        }
        tr {
            border-bottom: 1px solid black;
        }
        th, td {
            padding: 8px;
            padding-left: 0;
            text-align: left;
        }
        .width50 {
            width: 50%;
        }
        h1 {
            font-size: 1.3em;
        }
        h2 {
            font-size: 0.625rem;
            font-weight: 600;
            text-transform: uppercase;
            letter-spacing: 0.04em;
            color: #5A6079;
        }
        .offer-party-label {
            font-size: 1rem !important;
            line-height: 1rem !important;
            font-weight: 600 !important;
            text-transform: uppercase;
            letter-spacing: 0.025em;
            color: #5A6079 !important;
            margin: 0 0 0.5rem 0 !important;
        }
        .offer-footer {
            margin-top: 24px;
            padding-top: 16px;
            border-top: 1px solid #ccc;
            font-size: 0.75rem;
            color: #868686;
            line-height: 1.5;
            text-align: center;
        }
        .logo {
            padding-top: 11px;
        }
        @media print {
            .a4-wrapper {
                width: 190mm;
                margin: 10mm auto;
                page-break-after: always;
            }
        }
        @media screen {
            .a4-wrapper {
                width: 190mm;
                margin: 20px auto;
            }
        }
        section.price {
            padding-top: 8px;
            padding-bottom: 24px;
            padding-left: 50%;
            margin-bottom: 16px;
            display: flex;
            align-items: flex-start;
        }
        td.offer-note {
            padding-bottom: 17px;
            font-size: 90%;
            color: #868686;
        }
    </style>
</head>
<body>
    <div class="container a4-wrapper">
        <header>
            <div class="logo width50">
                <img src="${absoluteLogoUrl}" alt="Tiskárna Tria, s.r.o." width="140" height="45">
            </div>
            <section class="width50" style="text-align: right;">
                <h1>Cenová nabídka č. ${this.currentOffer.id}</h1>
                Ze dne: ${formattedDate}<br>
                Vystavil: ${this.currentOffer.createdBy || 'Systém'}<br>
            </section>
        </header>

        <section class="od">
            <div class="o width50">
                <p class="offer-party-label">Odběratel</p>
                <p>${this.customerData.replace(/\n/g, '<br>')}</p>
            </div>

            <div class="d width50">
                <p class="offer-party-label">Dodavatel</p>
                <p>Tiskárna Tria s.r.o.<br>
                Olbramovice Ves 31,<br>
                259 01 Olbramovice</p>
                <p>IČO: 49546112<br>
                DIČ: CZ49546112</p>
                <p>Email: tiskarna@tria.cz<br>
                Web: www.tria.cz</p>
            </div>
        </section>

        <table class="tabulka">
            <tr>
                <th>Označení položky</th>
                <th>Počet kusů</th>
                <th>Cena za kus</th>
                <th>Cena</th>
                <th>% DPH</th>
                <th>Cena s DPH</th>
            </tr>
            ${this.currentOffer.items.map(item => {
              const vatRate = 0.21;
              const priceWithVat = item.price * (1 + vatRate);
              const quantity = Number(item.formData?.pocetKusu) || 1;
              const unitPrice = item.pricePerPiece || (quantity > 0 ? item.price / quantity : 0);
              const shortName = stripFinishingFromName(item.name || '');
              const lines = item.formData ? buildFinishingLines(item.formData) : [];

              return `
                <tr>
                    <td>${shortName}${item.notes && item.notes.trim() ? `<br><span style="font-size: 90%; color: #868686;">${item.notes}</span>` : ''}</td>
                    <td>${quantity} ks</td>
                    <td>${unitPrice.toLocaleString('cs-CZ', { maximumFractionDigits: 2, minimumFractionDigits: 2 })} Kč</td>
                    <td>${item.price.toLocaleString('cs-CZ')} Kč</td>
                    <td>21 %</td>
                    <td>${priceWithVat.toLocaleString('cs-CZ')} Kč</td>
                </tr>
                ${lines.length ? `
                <tr>
                    <td colspan="6" class="offer-note">
                        ${lines.join('<br>')}
                    </td>
                </tr>
                ` : ''}
              `;
            }).join('')}
        </table>

        <section class="price">
            <div class="width50">
                <h2>Celková cena:</h2>
                ${totalPrice.toLocaleString('cs-CZ')} Kč
            </div>

            <div class="width50">
                <h2>Celková cena s DPH:</h2>
                ${totalPriceWithVat.toLocaleString('cs-CZ')} Kč
            </div>
        </section>

        <footer class="offer-footer">
            ${OFFER_FOOTER_DISCLAIMER}
        </footer>
    </div>
</body>
</html>
    `;
  }
}

// Export pro použití v app.js
window.OfferEditorManager = OfferEditorManager;